/**
 * Free Shipping Progress Bar - Dynamic Updates
 * Subscribes to cart updates and refreshes the progress bar
 * Supports US ($50) and International ($75) thresholds
 */

(function() {
  // Prevent duplicate initialization if script is loaded multiple times
  if (window.freeShippingProgressInitialized) return;
  window.freeShippingProgressInitialized = true;

  // Default threshold if not available (US threshold for safety)
  const DEFAULT_THRESHOLD = 5000; // $50 in cents

  // Store country code persistently to survive section replacements
  let persistedCountryCode = null;

  /**
   * Detect country code from DOM
   * Checks for hidden input fields in localization forms
   */
  function detectCountryCode() {
    // Check for hidden input in localization forms
    const countryInput = document.querySelector('input[name="country_code"]');
    if (countryInput && countryInput.value) {
      persistedCountryCode = countryInput.value;
      return countryInput.value;
    }
    // Return persisted value if DOM doesn't have it yet
    return persistedCountryCode;
  }

  /**
   * Determine shipping type and threshold based on country code
   */
  function getShippingInfo(countryCode) {
    const isUS = countryCode === 'US';
    return {
      threshold: isUS ? 5000 : 7500, // $50 for US, $75 for International
      shippingType: isUS ? 'US' : 'International'
    };
  }

  /**
   * Update progress bars with current cart subtotal
   */
  function updateProgressBars(subtotal) {
    const progressBars = document.querySelectorAll('.free-shipping-progress');
    if (progressBars.length === 0) return;

    // Always try to detect country code first (handles locale delay and section replacements)
    const detectedCountry = detectCountryCode();
    
    progressBars.forEach(bar => {
      // Get threshold and shipping type from data attributes
      let threshold = parseInt(bar.dataset.threshold, 10) || DEFAULT_THRESHOLD;
      let shippingType = bar.dataset.shippingType || 'US';
      let countryCode = bar.dataset.countryCode || '';

      // Always check if detected country differs from stored country code
      // This handles section replacements where HTML is refreshed but country code might be wrong
      if (detectedCountry && detectedCountry !== countryCode) {
        countryCode = detectedCountry;
        const shippingInfo = getShippingInfo(detectedCountry);
        threshold = shippingInfo.threshold;
        shippingType = shippingInfo.shippingType;
        
        // Update data attributes for future reference
        bar.dataset.countryCode = countryCode;
        bar.dataset.threshold = threshold;
        bar.dataset.shippingType = shippingType;
      } else if (!countryCode || countryCode === '') {
        // If country code is not set or empty, try to detect it from DOM
        // This handles the Shopify locale delay issue
        if (detectedCountry) {
          countryCode = detectedCountry;
          const shippingInfo = getShippingInfo(detectedCountry);
          threshold = shippingInfo.threshold;
          shippingType = shippingInfo.shippingType;
          
          // Update data attributes for future reference
          bar.dataset.countryCode = countryCode;
          bar.dataset.threshold = threshold;
          bar.dataset.shippingType = shippingType;
        }
      }

      // Calculate remaining amount and percentage
      const remaining = Math.max(0, threshold - subtotal);
      const percentage = Math.min(100, (subtotal / threshold) * 100);

      // Update the data attribute
      bar.dataset.subtotal = subtotal;

      // Update the message
      const messageEl = bar.querySelector('.free-shipping-progress__message');
      if (messageEl) {
        if (remaining > 0) {
          const formattedRemaining = (remaining / 100).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          messageEl.innerHTML = `<span class="free-shipping-progress__amount">${formattedRemaining}</span> away from free ${shippingType} shipping`;
          messageEl.classList.remove('free-shipping-progress__message--complete');
        } else {
          messageEl.innerHTML = `Free ${shippingType} shipping<span aria-hidden="true"> ✓</span><span class="visually-hidden"> unlocked</span>`;
          messageEl.classList.add('free-shipping-progress__message--complete');
        }
      }

      // Update the progress bar width (CSS handles positioning and colors)
      const fillBar = bar.querySelector('.free-shipping-progress__bar');
      if (fillBar) {
        fillBar.style.width = percentage + '%';
      }

      // Update ARIA attributes
      const container = bar.querySelector('.free-shipping-progress__bar-container');
      if (container) {
        container.setAttribute('aria-valuenow', subtotal);
        container.setAttribute('aria-valuemax', threshold);
        const formattedSubtotal = (subtotal / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        const formattedThreshold = (threshold / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        container.setAttribute('aria-label', `Free shipping progress: ${formattedSubtotal} of ${formattedThreshold}`);
      }
    });
  }

  /**
   * Fetch actual cart data and update progress bars
   * Also checks for country code updates to handle locale delay
   */
  function fetchCartAndUpdate() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        if (cart && typeof cart.items_subtotal_price !== 'undefined') {
          // Small delay to allow DOM to update after section replacement
          // This ensures country code detection happens after new HTML is inserted
          setTimeout(() => {
            updateProgressBars(cart.items_subtotal_price);
          }, 50);
        }
      })
      .catch(err => console.error('Error fetching cart:', err));
  }

  function initSubscription() {
    // Subscribe to cart updates using Shopify's pub/sub
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        // Always fetch fresh cart data since the event data may not include subtotal
        fetchCartAndUpdate();
      });
      return true;
    }
    return false;
  }

  // Try to subscribe immediately
  if (!initSubscription()) {
    // If pub/sub not ready, wait for DOMContentLoaded
    document.addEventListener('DOMContentLoaded', () => {
      if (!initSubscription()) {
        // Still not ready, try again after a short delay
        setTimeout(initSubscription, 100);
        setTimeout(initSubscription, 500);
        setTimeout(initSubscription, 1000);
      }
    });
  }

  // Also listen for custom cart events
  document.addEventListener('cart:updated', () => {
    fetchCartAndUpdate();
  });

  // Handle locale delay: check for country code updates periodically
  // This addresses Shopify's delay when reading locale info
  let localeCheckAttempts = 0;
  const maxLocaleChecks = 10; // Check up to 10 times (5 seconds total)
  
  function checkLocaleUpdate() {
    if (localeCheckAttempts >= maxLocaleChecks) return;
    
    const progressBars = document.querySelectorAll('.free-shipping-progress');
    let needsUpdate = false;
    
    progressBars.forEach(bar => {
      const currentCountryCode = bar.dataset.countryCode || '';
      const detectedCountry = detectCountryCode();
      
      // If country code was empty but is now available, we need to update
      if ((!currentCountryCode || currentCountryCode === '') && detectedCountry) {
        needsUpdate = true;
        const shippingInfo = getShippingInfo(detectedCountry);
        bar.dataset.countryCode = detectedCountry;
        bar.dataset.threshold = shippingInfo.threshold;
        bar.dataset.shippingType = shippingInfo.shippingType;
      }
    });
    
    if (needsUpdate) {
      // Fetch cart and update with new country info
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          if (cart && typeof cart.items_subtotal_price !== 'undefined') {
            updateProgressBars(cart.items_subtotal_price);
          }
        })
        .catch(err => console.error('Error fetching cart:', err));
    }
    
    localeCheckAttempts++;
    if (localeCheckAttempts < maxLocaleChecks) {
      setTimeout(checkLocaleUpdate, 500); // Check every 500ms
    }
  }

  // Start checking for locale updates after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(checkLocaleUpdate, 500);
    });
  } else {
    setTimeout(checkLocaleUpdate, 500);
  }

  // Watch for when progress bars are added/replaced in the DOM
  // This handles section replacements where HTML is refreshed
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          // Check if the added node is a progress bar or contains one
          const progressBar = node.classList?.contains('free-shipping-progress') 
            ? node 
            : node.querySelector?.('.free-shipping-progress');
          
          if (progressBar) {
            // Immediately fix country code after section replacement
            const detectedCountry = detectCountryCode();
            if (detectedCountry) {
              const shippingInfo = getShippingInfo(detectedCountry);
              progressBar.dataset.countryCode = detectedCountry;
              progressBar.dataset.threshold = shippingInfo.threshold;
              progressBar.dataset.shippingType = shippingInfo.shippingType;
              
              // Update the progress bar immediately if we have cart data
              fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                  if (cart && typeof cart.items_subtotal_price !== 'undefined') {
                    updateProgressBars(cart.items_subtotal_price);
                  }
                })
                .catch(() => {}); // Silently fail if cart fetch fails
            }
          }
        }
      });
    });
  });

  // Start observing the document for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
